import type { NavSection } from '@lilara/ui-web'

export interface SidebarNavData {
  sections: NavSection[]
  footerSections: NavSection[]
  navReloadMap: Map<string, boolean>
}

export const NAV_ITEM_IDS = {
  bots: 'bots',
  knowledgeBase: 'knowledge-base',
  evaluator: 'evaluator',
  inbox: 'inbox',
  realtimeDashboard: 'realtime-dashboard',
  caseArchive: 'case-archive',
  whatsappCallLogs: 'whatsapp-call-logs',
  contactsList: 'contacts-list',
  infoFields: 'info-fields',
  campaignsV2: 'campaigns-v2',
  campaigns: 'campaigns',
  campaignsContacts: 'campaigns-contacts',
  analyticsOverview: 'analytics-overview',
  analyticsAi: 'analytics-ai',
  analyticsCases: 'analytics-cases',
  analyticsAgents: 'analytics-agents',
  analyticsAutomation: 'analytics-automation',
  analyticsConversations: 'analytics-conversations',
  addOns: 'add-ons',
  settings: 'settings',
  helpCenter: 'help-center',
} as const

export interface SidebarPermissions {
  isAdmin: boolean
  isManager: boolean
  isAgent: boolean
  hasAiKnowledgeAccess: boolean
  hasQaEvaluatorAccess: boolean
  hasWhatsappCallLogsAccess: boolean
  hasCaseArchiveAccess: boolean
  hasContactsAccess: boolean
  showContactsListLink: boolean
  showCampaignsV2Nav: boolean
  hasCampaignsAccess: boolean
  hasAnalyticsAccess: boolean
  hasAnalyticsConversationsAccess: boolean
  hasCopilotAccess: boolean
  hasOrgSettingsAccess: boolean
}

export function buildSidebarNavData(
  perms: SidebarPermissions,
  translate: (key: string) => string = key => key
): SidebarNavData {
  const navReloadMap = new Map<string, boolean>()
  const sections: NavSection[] = []
  const footerItems: NavSection['items'] = []

  function reload(id: string, value: boolean) {
    navReloadMap.set(id, value)
  }

  // — Automation —
  if (perms.isAdmin) {
    const items: NavSection['items'] = []

    items.push({
      id: NAV_ITEM_IDS.bots,
      label: translate('shared.lilaraSidebar.nav.bots'),
      icon: 'message-bot',
      iconPrefix: 'far',
      href: '/automation/bots/all',
    })
    reload(NAV_ITEM_IDS.bots, false)

    if (perms.hasAiKnowledgeAccess) {
      items.push({
        id: NAV_ITEM_IDS.knowledgeBase,
        label: translate('shared.lilaraSidebar.nav.knowledgeBase'),
        icon: 'book-open',
        iconPrefix: 'far',
        href: '/automation/knowledgebase',
      })
      reload(NAV_ITEM_IDS.knowledgeBase, true)
    }

    sections.push({
      label: translate('shared.lilaraSidebar.nav.automation'),
      items,
    })
  }

  // — Evaluator —
  if (perms.hasQaEvaluatorAccess) {
    sections.push({
      label: translate('shared.lilaraSidebar.nav.evaluator'),
      items: [
        {
          id: NAV_ITEM_IDS.evaluator,
          label: translate('shared.lilaraSidebar.nav.evaluator'),
          icon: 'flask',
          iconPrefix: 'far',
          href: '/evaluator',
        },
      ],
    })
    reload(NAV_ITEM_IDS.evaluator, false)
  }

  // — Helpdesk —
  {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.inbox,
        label: translate('shared.lilaraSidebar.nav.inbox'),
        icon: 'headset',
        iconPrefix: 'far',
        href: '/desk/inbox',
      },
      {
        id: NAV_ITEM_IDS.realtimeDashboard,
        label: translate('shared.lilaraSidebar.nav.realtime'),
        icon: 'chart-line',
        iconPrefix: 'far',
        href: '/desk/dashboard',
      },
    ]
    reload(NAV_ITEM_IDS.inbox, false)
    reload(NAV_ITEM_IDS.realtimeDashboard, false)

    if (perms.hasCaseArchiveAccess) {
      items.push({
        id: NAV_ITEM_IDS.caseArchive,
        label: translate('shared.lilaraSidebar.nav.caseArchive'),
        icon: 'box-archive',
        iconPrefix: 'far',
        href: '/desk/archive',
      })
      reload(NAV_ITEM_IDS.caseArchive, false)
    }

    if (perms.hasWhatsappCallLogsAccess) {
      items.push({
        id: NAV_ITEM_IDS.whatsappCallLogs,
        label: translate('shared.lilaraSidebar.nav.callLogs'),
        icon: 'phone',
        iconPrefix: 'far',
        href: '/desk/whatsapp-call-logs',
      })
      reload(NAV_ITEM_IDS.whatsappCallLogs, false)
    }

    sections.push({
      label: translate('shared.lilaraSidebar.nav.helpdesk'),
      items,
    })
  }

  // — Contacts —
  if (perms.hasContactsAccess) {
    const items: NavSection['items'] = []

    if (perms.showContactsListLink) {
      items.push({
        id: NAV_ITEM_IDS.contactsList,
        label: translate('shared.lilaraSidebar.nav.contactList'),
        icon: 'address-card',
        iconPrefix: 'far',
        href: '/contacts/list',
      })
      reload(NAV_ITEM_IDS.contactsList, true)
    }

    items.push({
      id: NAV_ITEM_IDS.infoFields,
      label: translate('shared.lilaraSidebar.nav.infoFields'),
      icon: 'list-ul',
      href: '/contacts/info-fields',
    })
    reload(NAV_ITEM_IDS.infoFields, false)

    sections.push({
      label: translate('shared.lilaraSidebar.nav.contacts'),
      items,
    })
  }

  // — Campaigns V2 —
  if (perms.showCampaignsV2Nav) {
    sections.push({
      label: translate('shared.lilaraSidebar.nav.campaigns'),
      items: [
        {
          id: NAV_ITEM_IDS.campaignsV2,
          label: translate('shared.lilaraSidebar.nav.campaigns'),
          icon: 'bullhorn',
          iconPrefix: 'far',
          href: '/campaigns-v2/manage',
        },
      ],
    })
    reload(NAV_ITEM_IDS.campaignsV2, true)
  }

  // — Campaigns V1 —
  if (perms.hasCampaignsAccess) {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.campaigns,
        label: translate('shared.lilaraSidebar.nav.campaigns'),
        icon: 'bullhorn',
        iconPrefix: 'far',
        href: '/campaigns/manage',
      },
    ]
    reload(NAV_ITEM_IDS.campaigns, false)

    if (perms.isAdmin) {
      items.push({
        id: NAV_ITEM_IDS.campaignsContacts,
        label: translate('shared.lilaraSidebar.nav.campaignContacts'),
        icon: 'address-card',
        iconPrefix: 'far',
        href: '/campaigns/contacts',
      })
      reload(NAV_ITEM_IDS.campaignsContacts, false)
    }

    sections.push({
      label: translate('shared.lilaraSidebar.nav.campaigns'),
      items,
    })
  }

  // — Analytics —
  if (perms.hasAnalyticsAccess) {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.analyticsOverview,
        label: translate('shared.lilaraSidebar.nav.analyticsOverview'),
        icon: 'chart-bar',
        iconPrefix: 'far',
        href: '/analytics/overview',
      },
    ]
    reload(NAV_ITEM_IDS.analyticsOverview, false)

    if (perms.hasCopilotAccess) {
      items.push({
        id: NAV_ITEM_IDS.analyticsAi,
        label: translate('shared.lilaraSidebar.nav.aiAnalytics'),
        icon: 'robot',
        iconPrefix: 'far',
        href: '/analytics/copilot',
      })
      reload(NAV_ITEM_IDS.analyticsAi, false)
    }

    items.push(
      {
        id: NAV_ITEM_IDS.analyticsCases,
        label: translate('shared.lilaraSidebar.nav.analyticsCases'),
        icon: 'messages',
        iconPrefix: 'far',
        href: '/analytics/cases',
      },
      {
        id: NAV_ITEM_IDS.analyticsAgents,
        label: translate('shared.lilaraSidebar.nav.analyticsAgents'),
        icon: 'chart-user',
        iconPrefix: 'far',
        href: '/analytics/agents',
      },
      {
        id: NAV_ITEM_IDS.analyticsAutomation,
        label: translate('shared.lilaraSidebar.nav.automationMetrics'),
        icon: 'bolt-auto',
        iconPrefix: 'far',
        href: '/analytics/automation',
      }
    )
    reload(NAV_ITEM_IDS.analyticsCases, false)
    reload(NAV_ITEM_IDS.analyticsAgents, false)
    reload(NAV_ITEM_IDS.analyticsAutomation, false)

    if (perms.hasAnalyticsConversationsAccess) {
      items.push({
        id: NAV_ITEM_IDS.analyticsConversations,
        label: translate('shared.lilaraSidebar.nav.analyticsConversations'),
        icon: 'messages',
        iconPrefix: 'far',
        href: '/analytics/conversations',
      })
      reload(NAV_ITEM_IDS.analyticsConversations, false)
    }

    sections.push({
      label: translate('shared.lilaraSidebar.nav.analytics'),
      items,
    })
  }

  // — Footer —
  if (!perms.isAgent) {
    footerItems.push({
      id: NAV_ITEM_IDS.addOns,
      label: translate('shared.lilaraSidebar.nav.addOns'),
      icon: 'rocket-launch',
      iconPrefix: 'far',
      href: '/addons',
    })
    reload(NAV_ITEM_IDS.addOns, false)
  }

  if (perms.hasOrgSettingsAccess) {
    footerItems.push({
      id: NAV_ITEM_IDS.settings,
      label: translate('shared.lilaraSidebar.nav.settings'),
      icon: 'gear',
      iconPrefix: 'far',
      href: '/organization',
    })
    reload(NAV_ITEM_IDS.settings, false)
  }

  footerItems.push({
    id: NAV_ITEM_IDS.helpCenter,
    label: translate('shared.lilaraSidebar.nav.helpCenter'),
    icon: 'circle-question',
    iconPrefix: 'far',
    href: 'https://help.hubtype.com/',
    anchor: { target: '_blank', rel: 'noopener' },
  })
  // External link: native anchor navigation (target="_blank"), no reload
  // entry — the wrapper only intercepts reload=false client routes and
  // full-page reload=true React routes, never http(s) links.

  const footerSections: NavSection[] =
    footerItems.length > 0 ? [{ items: footerItems }] : []

  return { sections, footerSections, navReloadMap }
}
